import { Children, JSX, MouseEventHandler, ReactElement, Ref } from 'react';
import { APIScopeData, QueryBuilderQuery } from '@ardoq/api-types';
import { CustomSelectBase, setIsSelectAll } from '@ardoq/dropdown-menu';
import { actionNamespaceSelectContext } from '@ardoq/global-consts';
import { Locale } from '@ardoq/locale';
import { dispatchAction } from '@ardoq/rxbeach';
import styled from 'styled-components';
import { SelectContextRoot } from '../SelectContextRoot';
import {
  selectComponentSelectionChanged,
  setComponentSelectQuery,
} from '../../actions';
import {
  selectedComponentIdsOnOpeningDropdown,
  setFilterTerm,
} from '../../actions/navigatorActions';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { waitForComponentHierarchyToBeMounted } from './waitForComponentHierarchyToBeMounted';
import { isSameSet } from '@ardoq/common-helpers';
import { scopeDataOperations } from '@ardoq/scope-data';
import { Box, FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export type ContextSelectorTrackingCallbacks = {
  onDropdownOpened?: VoidFunction;
  onComponentSelected?: (numberOfComponents: number) => void;
  onSelectAllClicked?: VoidFunction;
};

export type ContextSelectorProps = {
  componentQuery?: QueryBuilderQuery | null;
  /**
   * Callback fired when components are selected in the dropdown.
   * Use scopeData.scopeComponents to get the selected component IDs.
   * @param scopeData
   */
  onComponentsSelected: (scopeData: APIScopeData, isSelectAll: boolean) => void;
  children?: ReactElement<{
    onClick?: MouseEventHandler;
    ref?: Ref<HTMLElement> | undefined;
    style?: React.CSSProperties;
  }>;
  minDropdownSize?: DropdownSize;
  selectedComponentIds?: string[];
  /**
   * When true, displays a search input at the top of the dropdown menu
   * and enables workspace filtering controls.
   */
  hasSearchInputInDropdown?: boolean;
  placeholderText?: string;
  selectedCount: number;
  onMenuOpen?: VoidFunction;
  /**
   * Optional tracking callbacks. These are called at user interaction points
   * so that tracking can be scoped to the place where ContextSelector is used.
   */
  tracking?: ContextSelectorTrackingCallbacks;
  locale: Locale;
  i18nLocale?: I18nLocale;
};

const StickyHeader = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const SelectAllAndCountRow = ({
  onSelectAll,
  count,
  i18nLocale,
}: {
  onSelectAll: () => void;
  count: number;
  i18nLocale: I18nLocale;
}) => (
  <StickyHeader padding="xxsmall" backgroundColor="surfaceDefault">
    <FlexBox justify="space-between" align="center">
      <Link hideIcon underlineOnHover typography="text2" onClick={onSelectAll}>
        {t(
          i18nLocale,
          'componentHierarchies:contextSelector.selectAll',
          'Select all'
        )}
      </Link>
      <Text variant="caption" color="textSubtle">
        {t(
          i18nLocale,
          'componentHierarchies:contextSelector.countSelected',
          { count },
          '{{count}} selected'
        )}
      </Text>
    </FlexBox>
  </StickyHeader>
);

export const ContextSelector = ({
  componentQuery,
  onComponentsSelected,
  children,
  minDropdownSize,
  selectedComponentIds,
  hasSearchInputInDropdown,
  placeholderText,
  selectedCount = 0,
  onMenuOpen,
  tracking,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ContextSelectorProps): JSX.Element => {
  const resolvedPlaceholderText =
    placeholderText ??
    t(
      i18nLocale,
      'componentHierarchies:componentSelector.placeholder',
      'Search for components'
    );

  const child = children ? Children.only(children) : undefined;

  const onOpened = async () => {
    dispatchAction(setFilterTerm(''), actionNamespaceSelectContext);

    try {
      await waitForComponentHierarchyToBeMounted(actionNamespaceSelectContext);

      if (!componentQuery) {
        return;
      }

      dispatchAction(setComponentSelectQuery({ query: componentQuery }));

      if (selectedComponentIds) {
        dispatchAction(
          selectedComponentIdsOnOpeningDropdown(selectedComponentIds)
        );
      }
    } catch (e) {
      // Timeout waiting for component hierarchy to be mounted.
      // Ignore as the dropdown can still open without it.
    }
    onMenuOpen?.();
    tracking?.onDropdownOpened?.();
  };

  const onComponentsSelectedWrapped = (scopeData: APIScopeData) => {
    const newSelection = scopeData.scopeComponents;
    // If the user opens the dropdown, we sync the selection in the dropdown
    // with the selectedComponentIds. This will trigger the internal callback,
    // which should not be propagated to the consumers of the component.
    if (selectedComponentIds && isSameSet(newSelection, selectedComponentIds)) {
      return;
    }
    onComponentsSelected(scopeData, false);
    tracking?.onComponentSelected?.(newSelection.length);
  };

  const onSelectAll = () => {
    onComponentsSelected(scopeDataOperations.getEmpty(), true);
    dispatchAction(setIsSelectAll(true));
    tracking?.onSelectAllClicked?.();
  };

  return (
    <CustomSelectBase
      placeholderText={resolvedPlaceholderText}
      onMenuOpen={onOpened}
      onOptionsChanged={onComponentsSelectedWrapped}
      optionsChangedAction={selectComponentSelectionChanged}
      actionNamespace={actionNamespaceSelectContext}
      onFilterChanged={onFilterChanged}
      selectButton={child}
      minDropdownSize={minDropdownSize}
      hasSearchInputInDropdown={hasSearchInputInDropdown}
    >
      <SelectAllAndCountRow
        onSelectAll={onSelectAll}
        count={selectedCount ?? 0}
        i18nLocale={i18nLocale}
      />
      <SelectContextRoot i18nLocale={i18nLocale} locale={locale} />
    </CustomSelectBase>
  );
};

const onFilterChanged = (filterTerm: string) =>
  dispatchAction(setFilterTerm(filterTerm), actionNamespaceSelectContext);
